//@flow

import React, { useEffect, useContext, useState } from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useField } from "react-final-form";
import { OnChange } from "react-final-form-listeners";

import { read, readAndReplace } from "modules/ChangeServiceForm/misc/utilities";

import { fetchProvidersByCategoryId } from "redux/actions/providers";
import { fetchTariffs } from "redux/actions/tariffs";
// import { getCurrentBaseTariffPrices } from "Commons/Modules/currentTariffPrices/api";

import { getProviders, getTariffs, getCategories } from "redux/sagas/selectors";

//import { shortOfferFormStepProgression } from "Commons/Utilities/GoogleTagManager";

import { mapProviders } from "Commons/Components/FormComponents/misc/Mappers";
import { PROVIDER_IS_NOT_IN_LIST } from "Commons/Modules/Provider/misc/Constants";
import Provider from "modules/ChangeServiceForm/components/fields/ProviderAndTariff/Provider";
import Tariff from "modules/ChangeServiceForm/components/fields/ProviderAndTariff/Tariff";
import Comment from "modules/ChangeServiceForm/components/fields/ProviderAndTariff/Comment";
import { mapTariffs } from "modules/ChangeServiceForm/components/fields/ProviderAndTariff/helpers";
import ShortFormSharedContext from "../../../misc/context";
// import SpecifiedTariffPrices from "./SpecifiedTariffPrices/SpecifiedTariffPrices";

import { Row, Label, Space } from "../../shared/CommonComponents";

const ProvidersAndTariffsHandler = () => {
  const {
    category: { categoryType },
    inheritedStyles: { formRow, formLabelLight, hidden },
    configurationData: {
      translations: { fieldLabels, glossary },
      initialValues,
    },
    activeCategory,
    steps: { stepName },
  } = useContext(ShortFormSharedContext);
  const dispatch = useDispatch();
  const providers = useSelector(getProviders);
  const tariffs = useSelector(getTariffs);
  const categories = useSelector(getCategories);

  /* Company type detection */
  const propertyTypeField = useField("propertyType");
  const isCompany = propertyTypeField?.input.value === "COMPANY" || false;

  /* Pre-selections */
  const providerIdPreSelection = initialValues[categoryType].categoryProviderId;
  const tariffIdPreSelection = initialValues[categoryType].permanentTariffId;

  /* Necessary fields */
  const providerIdField = useField("categoryProviderId");
  const providerIdFieldInputValue = providerIdField.input.value;
  const currentProviderName = useField("currentProviderName");
  const tariffIdField = useField("permanentTariffId");
  const currentTariff = useField("currentTariff");
  const currentTariffName = useField("currentTariffName");

  /* Fetch all providers & tariffs */
  useEffect(() => {
    const getProviders = async () => {
      if (activeCategory) {
        await dispatch(fetchProvidersByCategoryId(activeCategory.id));
        // Cases: (on step mount)
        // Provider selection is untouched or same as categoryProviderId initial value from the data json file, after interaction with the dropdown or during steps navigation
        if (providerIdPreSelection === providerIdFieldInputValue) {
          const categoryProvider = {
            categoryProviderId: providerIdPreSelection,
            categoryType,
          };
          // dispatch(fetchTariffs(categoryProvider, isCompany, "sof"));
          dispatch(fetchTariffs(categoryProvider, isCompany));
        }

        // Provider selection touched meaning different from the pre-selection from the data json file
        if (providerIdPreSelection !== providerIdFieldInputValue) {
          const categoryProvider = {
            categoryProviderId: providerIdFieldInputValue,
            categoryType,
          };
          // dispatch(fetchTariffs(categoryProvider, isCompany, "sof"));
          dispatch(fetchTariffs(categoryProvider, isCompany));
        }
      }
    };
    getProviders();
  }, []);

  /* Dropdown select options */
  // Provider
  const [selectedProvider, setSelectedProvider] = useState(
    // Stores the active provider in component state.
    // Preselects the provider from the initial values.
    providers?.providers[activeCategory?.id]?.find((provider) => provider.id === providerIdPreSelection)
  );

  useEffect(() => {
    setSelectedProvider(
      providers?.providers[activeCategory?.id]?.find((provider) => provider.id === providerIdField.input.value)
    );
  }, [providers, categories]);

  useEffect(() => {
    if (selectedProvider) {
      currentProviderName.input.onChange(selectedProvider.company?.companyName);
    }
  }, [selectedProvider]);

  // Tariff
  const [selectedTariff, setSelectedTariff] = useState(undefined);

  useEffect(() => {
    // When the tariffs data is ready
    if (tariffs?.tariffs.length > 0) {
      // Cases
      // Tariff selection is untouched or same as permanentTariffId initial value from the data json file, after or before interaction with the dropdown or during steps navigation
      if (tariffIdPreSelection && tariffIdPreSelection === tariffIdField.input.value) {
        setSelectedTariff(tariffs?.tariffs.find((tariff) => tariff.permanentId === tariffIdPreSelection));
      }

      // Tariff selection has been touched meaning now different from the initial value from the data json file or simply no initial value for permanentTariffId in the data json file
      if (!tariffIdPreSelection || tariffIdPreSelection !== tariffIdField.input.value) {
        setSelectedTariff(tariffs?.tariffs.find((tariff) => tariff.permanentId === tariffIdField.input.value));
      }
    }
  }, [tariffs]);

  const providersOptions =
    providers && activeCategory ? providers?.providers[activeCategory.id]?.map(mapProviders) : [];

  const tariffOptions = tariffs && tariffs.tariffs.length > 0 ? tariffs.tariffs.map(mapTariffs) : [];

  /* Current Prices API
  const [initialPriceDetails, setCurrentBaseTariffPrices] = useState({});
  const postalCodeField = useField("postalcode");
  const locationIdField = useField("locationId");
  const yearlyConsumptionField = useField(categoryType?.toLowerCase() + "YearlyConsumption");

  const currentPricesArguments = {
    category: activeCategory?.categoryType,
    postCode: postalCodeField?.input.value,
    locationId: locationIdField?.input.value,
    yearlyConsumption: yearlyConsumptionField?.input.value,
    tariffId: selectedTariff?.id === -9999 ? "" : selectedTariff?.id,
    permanentTariffId: tariffIdField?.input.value === -9999 ? "" : tariffIdField?.input.value,
  };

  useEffect(() => {
    async function fetchCurrentBaseTariffPrices() {
      if (selectedTariff) {
        currentTariff.input.onChange(selectedTariff.id);
        currentTariffName.input.onChange(selectedTariff.name);
        const initialPriceDetails = await getCurrentBaseTariffPrices(currentPricesArguments);
        setCurrentBaseTariffPrices({}); // Forces a refresh of the SpecifiedTariffPrices component.
        // If the API returns the same values, the SpecifiedTariffPrices component doesn't refresh
        // and in the case the user has changed the input values of PricePerUnit and FixedCosts
        // they won't be refreshed either
        setCurrentBaseTariffPrices(initialPriceDetails);
      }
    }
    fetchCurrentBaseTariffPrices();
  }, [selectedTariff]);
  */

  // @TODO: Must go when changes revert again
  useEffect(() => {
    currentTariff.input.onChange(selectedTariff?.id);
    currentTariffName.input.onChange(selectedTariff?.name);
    if (selectedTariff?.id !== -9999 && tariffIdField.input.value === -9999) {
      tariffIdField.input.onChange(undefined);
    }
  }, [selectedTariff]);

  return (
    <>
      <Row className={formRow}>
        <Label className={formLabelLight}>
          {readAndReplace(fieldLabels.providerAndTariff.providerId, {
            replace: (domNode) => {
              if (domNode.attribs && domNode.attribs.id === "replace") {
                return <span>{categoryType && glossary[categoryType.toLowerCase()].trans}</span>;
              }
            },
          })}
        </Label>
        <Provider options={providersOptions} loading={providers?.isFetching} stepName={stepName} />
      </Row>
      <Row className={classnames(formRow, { [hidden]: providerIdField.input.value === PROVIDER_IS_NOT_IN_LIST })}>
        <Label className={formLabelLight}>
          {readAndReplace(fieldLabels.providerAndTariff.permanentTariffId, {
            replace: (domNode) => {
              if (domNode.attribs && domNode.attribs.id === "replace") {
                return <span>{categoryType && glossary[categoryType.toLowerCase()].trans}</span>;
              }
            },
          })}
        </Label>
        <Tariff options={tariffOptions} loading={tariffs?.isFetching} stepName={stepName} />
      </Row>
      <Row className={classnames(formRow, { [hidden]: providerIdField.input.value !== PROVIDER_IS_NOT_IN_LIST })}>
        <Label className={formLabelLight}>{read(fieldLabels.providerAndTariff.comment)}</Label>
        <Comment fieldName="comment" stepName={stepName} />
      </Row>
      {/* <SpecifiedTariffPrices
        fieldLabels={fieldLabels}
        initialPriceDetails={initialPriceDetails || {}}
        consumption={yearlyConsumptionField?.input.value}
      /> */}
      <Space rem={0.25} />
      <OnChange name={"categoryProviderId"}>
        {(categoryProviderId) => {
          tariffIdField.input.onChange(undefined);
          currentTariff.input.onChange(undefined);
          currentTariffName.input.onChange(undefined);
          // Reset tariffs
          setSelectedProvider(
            providers?.providers[activeCategory.id].find((provider) => provider.id === categoryProviderId)
          );
          const categoryProvider = {
            categoryProviderId,
            categoryType,
          };
          // Get new Tariffs
          // dispatch(fetchTariffs(categoryProvider, isCompany, "sof"));
          dispatch(fetchTariffs(categoryProvider, isCompany));
        }}
      </OnChange>
      <OnChange name={"permanentTariffId"}>
        {(permanentTariffId) => {
          /*if (selectedTariff && selectedTariff !== -9999 && permanentTariffId === -9999) {
            shortOfferFormStepProgression("step_4_Tarifpreis");
          }*/
          setSelectedTariff(tariffs?.tariffs.find((tariff) => tariff.permanentId === permanentTariffId));
        }}
      </OnChange>
    </>
  );
};

export default ProvidersAndTariffsHandler;
